<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  TEAM_CHAT_NOTIFY,
  TEAM_CHAT_NOTIFY_KEY,
  teamChatNotifyPreference,
} from 'dashboard/helper/teamChatNotifier';

const { t } = useI18n();
const store = useStore();
const { updateUISettings } = useUISettings();

const OPTIONS = [
  { value: TEAM_CHAT_NOTIFY.ALL, icon: 'i-lucide-bell-ring', label: 'TEAM_CHAT.NOTIFY_ALL' },
  { value: TEAM_CHAT_NOTIFY.MENTIONS, icon: 'i-lucide-at-sign', label: 'TEAM_CHAT.NOTIFY_MENTIONS' },
  { value: TEAM_CHAT_NOTIFY.NONE, icon: 'i-lucide-bell-off', label: 'TEAM_CHAT.NOTIFY_NONE' },
];

const isOpen = ref(false);
const current = computed(() => teamChatNotifyPreference(store));
const currentIcon = computed(
  () => OPTIONS.find(option => option.value === current.value)?.icon
);

const supportsDesktop = 'Notification' in window;
const desktopPermission = ref(supportsDesktop ? Notification.permission : 'denied');

const choose = value => {
  updateUISettings({ [TEAM_CHAT_NOTIFY_KEY]: value });
};
const enableDesktop = async () => {
  desktopPermission.value = await Notification.requestPermission();
};
</script>

<template>
  <!-- The menu anchors to the sidebar header (relative), not to this button:
       anchored to the button's right edge it overflowed the left of the sidebar. -->
  <div>
    <button
      type="button"
      class="flex items-center justify-center rounded-lg size-8 text-n-slate-11 hover:bg-n-alpha-2"
      :title="t('TEAM_CHAT.NOTIFY_TITLE')"
      @click="isOpen = !isOpen"
    >
      <span class="size-4" :class="currentIcon" />
    </button>
    <div
      v-if="isOpen"
      v-on-click-outside="() => (isOpen = false)"
      class="absolute z-30 p-1.5 border shadow-lg start-3 end-3 top-full rounded-xl bg-n-solid-1 border-n-weak"
    >
      <p
        class="px-2 pt-1 pb-1.5 text-[0.6875rem] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        {{ t('TEAM_CHAT.NOTIFY_TITLE') }}
      </p>
      <button
        v-for="option in OPTIONS"
        :key="option.value"
        type="button"
        class="flex items-center w-full gap-2.5 px-2 py-2 text-sm rounded-lg text-start hover:bg-n-alpha-2"
        :class="current === option.value ? 'text-n-slate-12 font-medium' : 'text-n-slate-11'"
        @click="choose(option.value)"
      >
        <span class="flex-shrink-0 size-4" :class="option.icon" />
        <span class="flex-1">{{ t(option.label) }}</span>
        <span
          v-if="current === option.value"
          class="flex-shrink-0 size-4 i-lucide-check text-n-brand"
        />
      </button>
      <div v-if="supportsDesktop" class="pt-1.5 mt-1.5 border-t border-n-weak">
        <button
          v-if="desktopPermission === 'default'"
          type="button"
          class="flex items-center w-full gap-2.5 px-2 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
          @click="enableDesktop"
        >
          <span class="flex-shrink-0 size-4 i-lucide-monitor" />
          {{ t('TEAM_CHAT.NOTIFY_DESKTOP') }}
        </button>
        <p
          v-else
          class="flex items-start gap-2.5 px-2 py-2 text-xs text-n-slate-11"
        >
          <span
            class="flex-shrink-0 mt-px size-4"
            :class="
              desktopPermission === 'granted'
                ? 'i-lucide-monitor-check text-n-teal-11'
                : 'i-lucide-monitor-x text-n-ruby-11'
            "
          />
          {{
            desktopPermission === 'granted'
              ? t('TEAM_CHAT.NOTIFY_DESKTOP_ON')
              : t('TEAM_CHAT.NOTIFY_DESKTOP_BLOCKED')
          }}
        </p>
      </div>
    </div>
  </div>
</template>
